/**
 * NotificationContext - Centralized messaging adapter
 * 
 * This context provides a single decision point for all user notifications.
 * Routes messages to either:
 * - Modal (user-meaningful, requires acknowledgement)
 * - Network Error Modal (with retry functionality)
 * - Silent log (operational, debug-only)
 * - Nothing (noise to be removed)
 * 
 * POLICY:
 * - Modals: Payment/billing, account/access, data loss, compliance, blocking errors
 * - Network Error: Connection failures with automatic retry
 * - Silent: Saved, loaded, refreshing, background jobs, success when UI reflects result
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import AcknowledgementModal from '../components/AcknowledgementModal';
import NetworkErrorModal from '../components/NetworkErrorModal';

const NotificationContext = createContext(null);

// Message categories that should show modals
const MODAL_CATEGORIES = [
  'billing',      // Payment / credits / purchase
  'account',      // Auth, verification, permissions
  'data_loss',    // Unsaved changes, deletions
  'compliance',   // TOS, policy updates
  'error',        // Blocking system errors
  'security',     // Security warnings
];

export const NotificationProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    severity: 'info',
    buttonText: 'OK',
    onButtonClick: null,
  });

  // Network error modal state
  const [networkErrorState, setNetworkErrorState] = useState({
    isOpen: false,
    title: 'Network Error',
    message: 'Unable to connect to the server. Please check your connection.',
    onRetry: null,
    autoRetry: true,
  });

  // Queue for multiple notifications (show one at a time)
  const [notificationQueue, setNotificationQueue] = useState([]);
  
  // Store pending retry function reference
  const pendingRetryRef = useRef(null);

  /**
   * Show a modal notification (user-meaningful messages only)
   * 
   * @param {Object} options
   * @param {string} options.title - Modal title
   * @param {string} options.message - Modal body message
   * @param {string} options.severity - 'error' | 'warning' | 'success' | 'info' | 'billing' | 'account' | 'security'
   * @param {string} options.buttonText - Button text (default: 'OK')
   * @param {Function} options.onButtonClick - Optional callback when OK is clicked
   */
  const notifyModal = useCallback(({ title, message, severity = 'info', buttonText = 'OK', onButtonClick = null }) => {
    const notification = { title, message, severity, buttonText, onButtonClick };
    
    if (!modalState.isOpen) {
      // Show immediately if no modal is open
      setModalState({ ...notification, isOpen: true });
    } else {
      // Queue if a modal is already showing
      setNotificationQueue(prev => [...prev, notification]);
    }
  }, [modalState.isOpen]);

  /**
   * Close the current modal and show next in queue if any
   */
  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
    
    // Show next notification in queue after a brief delay
    setTimeout(() => {
      setNotificationQueue(prev => {
        if (prev.length > 0) {
          const [next, ...rest] = prev;
          setModalState({ ...next, isOpen: true });
          return rest;
        }
        return prev;
      });
    }, 300);
  }, []);

  /**
   * Log to console only (for operational/debug messages)
   * Only shows in development mode
   */
  const notifySilentLog = useCallback((message, context = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[NewAudia]', message, context);
    }
  }, []);

  /**
   * Do nothing - for messages that should be completely removed
   */
  const notifyNone = useCallback(() => {
    // Intentionally empty - used to mark removed notifications
  }, []);

  // ============================================================================
  // CONVENIENCE METHODS (map to specific severity/category)
  // ============================================================================

  /**
   * Show error modal (blocking errors)
   */
  const notifyError = useCallback((message, title = 'Error') => {
    notifyModal({ title, message, severity: 'error' });
  }, [notifyModal]);

  /**
   * Show billing/credits modal
   */
  const notifyBilling = useCallback((message, title = 'Reader Credits') => {
    notifyModal({ title, message, severity: 'billing' });
  }, [notifyModal]);

  /**
   * Show account/auth modal
   */
  const notifyAccount = useCallback((message, title = 'Account') => {
    notifyModal({ title, message, severity: 'account' });
  }, [notifyModal]);

  /**
   * Show warning modal
   */
  const notifyWarning = useCallback((message, title = 'Warning') => {
    notifyModal({ title, message, severity: 'warning' });
  }, [notifyModal]);

  /**
   * Show success modal (only for significant user achievements)
   */
  const notifySuccess = useCallback((message, title = 'Success') => {
    notifyModal({ title, message, severity: 'success' });
  }, [notifyModal]);

  /**
   * Show info modal
   */
  const notifyInfo = useCallback((message, title = 'Information') => {
    notifyModal({ title, message, severity: 'info' });
  }, [notifyModal]);

  /**
   * Show security warning modal
   */
  const notifySecurity = useCallback((message, title = 'Security Alert') => {
    notifyModal({ title, message, severity: 'security' });
  }, [notifyModal]);

  /**
   * Show network error modal with retry functionality
   * 
   * @param {Object} options
   * @param {string} options.message - Error message to display
   * @param {Function} options.onRetry - Async function to retry the failed operation
   * @param {string} options.title - Modal title (default: 'Network Error')
   * @param {boolean} options.autoRetry - Whether to auto-retry (default: true)
   */
  const notifyNetworkError = useCallback(({ 
    message = 'Unable to connect to the server. Please check your connection.',
    onRetry,
    title = 'Network Error',
    autoRetry = true
  }) => {
    // Store the retry function
    pendingRetryRef.current = onRetry;
    
    setNetworkErrorState({
      isOpen: true,
      title,
      message,
      onRetry,
      autoRetry
    });
  }, []);

  /**
   * Close network error modal
   */
  const closeNetworkError = useCallback(() => {
    setNetworkErrorState(prev => ({ ...prev, isOpen: false }));
    pendingRetryRef.current = null;
  }, []);

  /**
   * Check if any network error is currently showing
   */
  const hasNetworkError = networkErrorState.isOpen;

  const value = {
    // Core methods
    notifyModal,
    notifySilentLog,
    notifyNone,
    
    // Convenience methods
    notifyError,
    notifyBilling,
    notifyAccount,
    notifyWarning,
    notifySuccess,
    notifyInfo,
    notifySecurity,
    
    // Network error methods
    notifyNetworkError,
    closeNetworkError,
    hasNetworkError,
    
    // For checking if a modal is open
    isModalOpen: modalState.isOpen || networkErrorState.isOpen,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <AcknowledgementModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        severity={modalState.severity}
        buttonText={modalState.buttonText}
        onButtonClick={modalState.onButtonClick}
      />
      <NetworkErrorModal
        isOpen={networkErrorState.isOpen}
        onClose={closeNetworkError}
        onRetry={networkErrorState.onRetry}
        title={networkErrorState.title}
        message={networkErrorState.message}
        autoRetry={networkErrorState.autoRetry}
      />
    </NotificationContext.Provider>
  );
};

/**
 * Hook to access notification methods
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
